import { useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { AiOutlineClose, AiOutlineCheck } from "react-icons/ai";
import {
    MEDIA_BTN_DELETE_MD,
    MEDIA_BTN_DELETE_SM,
} from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import ConfirmPopUp from "../../popup/confirmPopUp";

const choiceLabels = ["ก", "ข", "ค", "ง", "จ", "ฉ", "ช", "ซ", "ฌ", "ญ", "ฎ", "ฏ"];

type Answer = {
    id: number;
    text: string;
    isCorrect: boolean;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
};

type Question = {
    id: number;
    text: string;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    hasHint: boolean;
    hintText: string;
    hintImageFileName: string;
    hintImageFile: File | null;
    hintImageUrl: string | null;
    hintAudioFileName: string;
    hintAudioFile: File | null;
    hintAudioUrl: string | null;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
    answers: Answer[];
};

type QuestionBlockProps = {
    index: number;
    question: Question;
    onQuestionTextChange: (questionId: number, value: string) => void;
    onQuestionHintTextChange: (questionId: number, value: string) => void;
    onQuestionHintImageFileChange: (questionId: number, file: File) => void;
    onQuestionHintAudioFileChange: (questionId: number, file: File) => void;
    onEnableQuestionHint: (questionId: number) => void;
    onClearQuestionHint: (questionId: number) => void;
    onQuestionImageFileChange: (questionId: number, file: File) => void;
    onQuestionAudioFileChange: (questionId: number, file: File) => void;
    onAnswerTextChange: (questionId: number, answerId: number, value: string) => void;
    onAnswerImageFileChange: (questionId: number, answerId: number, file: File) => void;
    onAnswerAudioFileChange: (questionId: number, answerId: number, file: File) => void;
    onToggleAnswerCorrect: (questionId: number, answerId: number) => void;
    onAddAnswer: (questionId: number) => void;
    onRemoveAnswer: (questionId: number, answerId: number) => void | Promise<void>;
    onRemoveQuestion: (questionId: number) => void | Promise<void>;
    onMoveQuestionUp: (questionId: number) => void;
    onMoveQuestionDown: (questionId: number) => void;
    onQuestionSkillChange: (
        questionId: number,
        skillKey:
            | "skill_listening_speaking"
            | "skill_lang_nature"
            | "skill_reading"
            | "skill_writing",
        checked: boolean
    ) => void;
    onSelectQuestion: (questionId: number) => void;
    isSelected: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
};

export default function QuestionBlock({
    index,
    question,
    onQuestionTextChange,
    onQuestionHintTextChange,
    onQuestionHintImageFileChange,
    onQuestionHintAudioFileChange,
    onEnableQuestionHint,
    onClearQuestionHint,
    onQuestionImageFileChange,
    onQuestionAudioFileChange,
    onAnswerTextChange,
    onAnswerImageFileChange,
    onAnswerAudioFileChange,
    onToggleAnswerCorrect,
    onAddAnswer,
    onRemoveAnswer,
    onRemoveQuestion,
    onMoveQuestionUp,
    onMoveQuestionDown,
    onQuestionSkillChange,
    onSelectQuestion,
    isSelected,
    canMoveUp,
    canMoveDown,
}: QuestionBlockProps) {
    const canAddMoreAnswer = question.answers.length < choiceLabels.length;
    const questionImageInputId = `question-image-${question.id}`;
    const questionAudioInputId = `question-audio-${question.id}`;
    const questionHintImageInputId = `question-hint-image-${question.id}`;
    const questionHintAudioInputId = `question-hint-audio-${question.id}`;
    const [pendingDelete, setPendingDelete] = useState<null | { type: "question" } | { type: "answer"; answerId: number }>(null);

    return (
        <div className="mb-4 flex w-full items-start gap-2" onClick={() => onSelectQuestion(question.id)}>
            <div className="w-[40px] shrink-0 pt-5 text-center">
                <span className="text-lg font-semibold text-[#5a5a5a]">{index}.</span>
            </div>
            <div className="flex-1 rounded-lg border border-[#e5e5e5] bg-[#fafafa] p-5 transition-colors shadow-md">

                <div className="mb-2 flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#555]">คำถาม</label>
                    <div className="relative">
                        <div className="flex h-[38px] items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                            <input
                                value={question.text}
                                onChange={(event) => onQuestionTextChange(question.id, event.target.value)}
                                className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                            />
                            <div className="flex items-center gap-1 pl-2">
                                <MediaEditGroup
                                    imageInputId={questionImageInputId}
                                    imageFile={question.imageFile}
                                    imageUrl={question.imageUrl}
                                    imageAlt="รูปคำถาม"
                                    onImageChange={(file) => onQuestionImageFileChange(question.id, file)}
                                    audioInputId={questionAudioInputId}
                                    audioFile={question.audioFile}
                                    audioUrl={question.audioUrl}
                                    onAudioChange={(file) => onQuestionAudioFileChange(question.id, file)}
                                />
                            </div>
                        </div>
                    </div>

                    {question.hasHint ? (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-bold text-[#555]">คำใบ้</label>
                            <div className="relative">
                                <div className="flex h-[38px] items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                    <input
                                        value={question.hintText}
                                        onChange={(event) => onQuestionHintTextChange(question.id, event.target.value)}
                                        className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                                        placeholder="พิมพ์คำใบ้ (ไม่บังคับ)"
                                    />
                                    <div className="flex items-center gap-1 pl-2">
                                        <MediaEditGroup
                                            imageInputId={questionHintImageInputId}
                                            imageFile={question.hintImageFile}
                                            imageUrl={question.hintImageUrl}
                                            imageAlt="รูปคำใบ้"
                                            onImageChange={(file) => onQuestionHintImageFileChange(question.id, file)}
                                            audioInputId={questionHintAudioInputId}
                                            audioFile={question.hintAudioFile}
                                            audioUrl={question.hintAudioUrl}
                                            onAudioChange={(file) => onQuestionHintAudioFileChange(question.id, file)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => onClearQuestionHint(question.id)}
                                            className={MEDIA_BTN_DELETE_MD}
                                            title="ล้างคำใบ้"
                                        >
                                            <MdDeleteOutline size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onEnableQuestionHint(question.id)}
                            className="cursor-pointer self-start text-xs font-medium text-[#4ca2e2] hover:text-blue-600 hover:underline"
                        >
                            + เพิ่มคำใบ้ (ตัวเลือก)
                        </button>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#555]">คำตอบ</label>
                    {question.answers.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                        {question.answers.map((answer, answerIndex) => {
                            const answerImageInputId = `answer-image-${question.id}-${answer.id}`;
                            const answerAudioInputId = `answer-audio-${question.id}-${answer.id}`;

                            return (
                                <div
                                    key={answer.id}
                                    className="flex h-[38px] items-center gap-2 rounded-md bg-white px-3"
                                >
                                    <span className="inline-flex min-h-7 min-w-7 shrink-0 items-center justify-center rounded-md border border-[#cfe8f7] bg-[#eef6fc] px-2 text-sm font-bold text-[#555] shadow-sm">
                                        {choiceLabels[answerIndex] ?? `${answerIndex + 1}.`}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={() => onToggleAnswerCorrect(question.id, answer.id)}
                                        title={answer.isCorrect ? "คำตอบถูก — คลิกเพื่อเปลี่ยน" : "คำตอบผิด — คลิกเพื่อเปลี่ยน"}
                                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-sm font-bold shadow-sm transition-colors cursor-pointer ${
                                            answer.isCorrect
                                                ? "border-[#15B427]/45 bg-[#e8f8ea] text-[#15B427] hover:bg-[#dcf5df]"
                                                : "border-[#ffb3b3] bg-[#fff5f5] text-[#e02020] hover:bg-[#ffecec]"
                                        }`}
                                    >
                                        {answer.isCorrect ? "✓" : "✕"}
                                    </button>

                                    <input
                                        value={answer.text}
                                        onChange={(event) => onAnswerTextChange(question.id, answer.id, event.target.value)}
                                        className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                                    />

                                    <div className="flex shrink-0 items-center gap-1 pl-2">
                                        <MediaEditGroup
                                            imageInputId={answerImageInputId}
                                            imageFile={answer.imageFile}
                                            imageUrl={answer.imageUrl}
                                            imageAlt={`รูปตัวเลือก ${choiceLabels[answerIndex] ?? answerIndex + 1}`}
                                            onImageChange={(file) => onAnswerImageFileChange(question.id, answer.id, file)}
                                            audioInputId={answerAudioInputId}
                                            audioFile={answer.audioFile}
                                            audioUrl={answer.audioUrl}
                                            onAudioChange={(file) => onAnswerAudioFileChange(question.id, answer.id, file)}
                                            size="sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setPendingDelete({ type: "answer", answerId: answer.id })}
                                            className={`${MEDIA_BTN_DELETE_SM} ml-0.5`}
                                            title="ลบ"
                                        >
                                            <MdDeleteOutline size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    ) : null}

                    <div className="mt-1 flex justify-center">
                        <button
                            type="button"
                            onClick={() => onAddAnswer(question.id)}
                            disabled={!canAddMoreAnswer}
                            className="cursor-pointer rounded-md bg-[#ffeb3b] px-4 py-1.5 text-sm font-semibold text-[#333] hover:bg-[#f4d735] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            + เพิ่มคำตอบ
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#555]">สมรรถนะ:</label>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={question.skill_listening_speaking}
                                onChange={(event) => onQuestionSkillChange(question.id, "skill_listening_speaking", event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            การฟัง การดู และการพูดเพื่อพัฒนาการคิด
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={question.skill_reading}
                                onChange={(event) => onQuestionSkillChange(question.id, "skill_reading", event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            การอ่านเพื่อพัฒนาการคิด
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={question.skill_lang_nature}
                                onChange={(event) => onQuestionSkillChange(question.id, "skill_lang_nature", event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            การเข้าใจธรรมชาติของภาษาไทยและการใช้ภาษาไทย
                        </label>
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={question.skill_writing}
                                    onChange={(event) => onQuestionSkillChange(question.id, "skill_writing", event.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc]"
                                />
                                การเขียนเพื่อพัฒนาการคิด
                            </label>
                                <button
                                type="button"
                                    onClick={() => setPendingDelete({ type: "question" })}
                                className={MEDIA_BTN_DELETE_MD}
                                title="ลบคำถาม"
                            >
                                <MdDeleteOutline size={18} />
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            <div className="flex w-[40px] shrink-0 flex-col items-center gap-1 pt-5 text-[#555]">
                <button
                    type="button"
                    onClick={() => onMoveQuestionUp(question.id)}
                    disabled={!canMoveUp}
                    className="cursor-pointer text-xl hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                    title="เลื่อนขึ้น"
                >
                    ↑
                </button>
                <button
                    type="button"
                    onClick={() => onMoveQuestionDown(question.id)}
                    disabled={!canMoveDown}
                    className="cursor-pointer text-xl hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                    title="เลื่อนลง"
                >
                    ↓
                </button>
            </div>
            {pendingDelete ? (
                <ConfirmPopUp
                    title="ยืนยันการลบ"
                    message={pendingDelete.type === "question" ? "ต้องการลบคำถามนี้หรือไม่" : "ต้องการลบตัวเลือกนี้หรือไม่"}
                    cancelText="ยกเลิก"
                    confirmText="ตกลง"
                    onCancel={() => setPendingDelete(null)}
                    onConfirm={async () => {
                        if (pendingDelete.type === "question") {
                            await onRemoveQuestion(question.id);
                        } else {
                            await onRemoveAnswer(question.id, pendingDelete.answerId);
                        }
                        setPendingDelete(null);
                    }}
                />
            ) : null}
        </div>
    );
}
